import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import * as DateHelpers from './DateHelpers';

interface EditNotesProps {
    systemCompanyId: number;
    scheduleId: number;
    editDriverNotes: boolean;
    showDriverScheduleNotes: boolean;   //Plan Changes and Runsheet Screen - I just want the Schedule and Driver notes to show/edit
    showEditNotesModal: boolean;
    hideModal(): void;
    save(scheduleNotes: string, timestamp: Uint8Array): void;
}

interface EditNotes {
    id: number;
    jobId: number;
    item: string;
    truckAccess: number;
    parkingAvail: number;
    siteClean: number;
    powerlines: number;
    weekdayAccess: number;
    weekendAccess: number;
    phoneCallRequired: number;
    tiltSlideClearance: number;
    plumbingElectricityDisconnected: number;
    isEvent: boolean;
    eventStartDate: string;
    eventEndDate: string;
    deliveryWindowStart: string;
    deliveryWindowEnd: string;
    deliveryPlacementNotes: string;
    extras: string;
    isEws: boolean;
    notes: string;
    runsheetAutoNotes: string;      //notes that I auto put on the Runsheet
    runsheetNotes: string;
    driverNotes: string;
}

interface GenericList {
    id: number;
    name: string;
}

const EditNoteModal = (props: EditNotesProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [editNotes, setEditNotes] = React.useState<EditNotes>({
        id: 0,
        jobId: 0,
        item: "",
        truckAccess: -1,
        parkingAvail: -1,
        siteClean: -1,
        powerlines: -1,
        weekdayAccess: -1,
        weekendAccess: -1,
        phoneCallRequired: -1,
        tiltSlideClearance: -1,
        plumbingElectricityDisconnected: -1,
        isEvent: false,
        eventStartDate: "",
        eventEndDate: "",
        deliveryWindowStart: "",
        deliveryWindowEnd: "",
        deliveryPlacementNotes: "",
        extras: "",
        isEws: false,
        notes: "",
        runsheetAutoNotes: "",
        runsheetNotes: "",
        driverNotes: ""
    });
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);

    React.useEffect(() => {
        if (props.scheduleId > 0 && props.showEditNotesModal) {
            getData(props.scheduleId);
        }
    }, [props.scheduleId]);

    const getData = async (scheduleId: number) => {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetEditNotes?ScheduleId=' + scheduleId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setEditNotes(res.data.editNotesItem);
            setYesNoOptions(res.data.yesNoOptions);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const resetData = () => {
        var editItem: EditNotes = {
            id: 0,
            jobId: 0,
            item: "",
            truckAccess: -1,
            parkingAvail: -1,
            siteClean: -1,
            powerlines: -1,
            weekdayAccess: -1,
            weekendAccess: -1,
            phoneCallRequired: -1,
            tiltSlideClearance: -1,
            plumbingElectricityDisconnected: -1,
            isEvent: false,
            eventStartDate: "",
            eventEndDate: "",
            deliveryWindowStart: "",
            deliveryWindowEnd: "",
            deliveryPlacementNotes: "",
            extras: "",
            isEws: false,
            notes: "",
            runsheetAutoNotes: "",
            runsheetNotes: "",
            driverNotes: ""
        };
        return editItem;
    }
    

    const hideEditNotesModal = () => {
        var editItem = resetData();
        setEditNotes(editItem);
        props.hideModal();
    }

    const handleEventChange = (e: any) => {
        e.preventDefault();

        setEditNotes(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleNotesChange = (e: any) => {
        e.preventDefault();

        setEditNotes(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleNumberChange = (e: any) => {
        e.preventDefault();

        var value = e.target.value * 1;

        setEditNotes(prevState => ({ ...prevState, [e.target.name]: value }));
    }

    const validate = () => {
        let errorList: any = {};
        var formIsValid = true;

        //need to validate event dates
        if (editNotes.isEvent) {
            if (!editNotes.eventStartDate || editNotes.eventStartDate === "") {
                formIsValid = false;
                errorList["eventStartDate"] = "Event Start Date is required";
            } else if (!DateHelpers.isValidDate(editNotes.eventStartDate)) {
                formIsValid = false;
                errorList["eventStartDate"] = "Valid Event Start Date is required";
            }

            if (!editNotes.eventEndDate || editNotes.eventEndDate === "") {
                formIsValid = false;
                errorList["eventEndDate"] = "Event End Date is required";
            } else if (!DateHelpers.isValidDate(editNotes.eventEndDate)) {
                formIsValid = false;
                errorList["eventEndDate"] = "Valid Event End Date is required";
            } else if (!DateHelpers.compareDateGreater(editNotes.eventStartDate, editNotes.eventEndDate)) {
                formIsValid = false;
                errorList["eventEndDate"] = "Event End Date > Event Start Date";
            }

            if (!editNotes.deliveryWindowStart || editNotes.deliveryWindowStart === "") {
                formIsValid = false;
                errorList["deliveryWindowStart"] = "Delivery Window Start is required";
            } else if (!DateHelpers.isValidDate(editNotes.deliveryWindowStart)) {
                formIsValid = false;
                errorList["deliveryWindowStart"] = "Valid Delivery Window Start is required";
            }

            if (!editNotes.deliveryWindowEnd || editNotes.deliveryWindowEnd === "") {
                formIsValid = false;
                errorList["deliveryWindowEnd"] = "Delivery Window End is required";
            } else if (!DateHelpers.isValidDate(editNotes.deliveryWindowEnd)) {
                formIsValid = false;
                errorList["deliveryWindowEnd"] = "Valid Delivery Window End is required";
            } else if (!DateHelpers.compareDateGreater(editNotes.deliveryWindowStart, editNotes.deliveryWindowEnd)) {
                formIsValid = false;
                errorList["deliveryWindowEnd"] = "Delivery Window End > Delivery Window Start";
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveNotes();
        } else {
            toast.error("Please fix errors on screen before saving!");
        }
    }

    const saveNotes = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var saveNoteItem = {
            scheduleId: props.scheduleId,
            saveDriverNotes: props.editDriverNotes,
            editNoteItem: editNotes,
            subId: user.sub
        };

        //save
        axios.post('api/Schedule/SaveEditNoteItem', saveNoteItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            //reload the day
            var editItem = resetData();
            setEditNotes(editItem);

            props.save(res.data.scheduleNotes, res.data.rowVersionTimestamp);
        })
        .catch(error => {
            toast.error(error.message);
        }); 
    }

    const renderDetails = (
        <Modal show={props.showEditNotesModal} onHide={hideEditNotesModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Edit Notes for {editNotes.item}</Modal.Title>
                <button type="button" className="close" onClick={hideEditNotesModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div className={editNotes.jobId === 0 || props.showDriverScheduleNotes ? "hidden" : "" }>
                    <div className={props.systemCompanyId === 3 ? "hidden" : ""}>
                        <div>
                            <label className="input-group" htmlFor="truck">
                                <span className={"label" + (editNotes.truckAccess === 0 ? " makeitred" : "")}>Truck Access?</span>
                                <select className={"select" + (editNotes.truckAccess === 0 ? " makeitred" : "")} id="truckAccess" name="truckAccess" value={editNotes.truckAccess} onChange={(e) => handleNumberChange(e)}>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="parkingAvail">
                                <span className={"label" + (editNotes.parkingAvail === 0 ? " makeitred" : "")}>Parking Avail?</span>
                                <select className={"select" + (editNotes.parkingAvail === 0 ? " makeitred" : "")} id="parkingAvail" name="parkingAvail" value={editNotes.parkingAvail} onChange={(e) => handleNumberChange(e)}>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="siteClean">
                                <span className={"label" + (editNotes.siteClean === 0 ? " makeitred" : "")}>Is Site {props.systemCompanyId === 2 ? "Clean" : "Level"}?</span>
                                <select className={"select" + (editNotes.siteClean === 0 ? " makeitred" : "")} id="siteclean" name="siteClean" value={editNotes.siteClean} onChange={(e) => handleNumberChange(e)}>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="input-group" htmlFor="powerlines">
                            <span className={"label" + (editNotes.powerlines === 1 ? " makeitred" : "")}>Powerlines within 4.6m?</span>
                            <select className={"select" + (editNotes.powerlines === 1 ? " makeitred" : "")} id="powerlines" name="powerlines" value={editNotes.powerlines} onChange={(e) => handleNumberChange(e)}>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                    </div>
                    <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="weekdayAccess">
                            <span className={"label" + (editNotes.weekdayAccess === 0 ? " makeitred" : "")}>Is there clear access between 7am and 3pm weekdays?</span>
                            <select className={"select" + (editNotes.weekdayAccess === 0 ? " makeitred" : "")} id="weekdayAccess" name="weekdayAccess" value={editNotes.weekdayAccess} onChange={(e) => handleNumberChange(e)}>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>

                        <label className="input-group" htmlFor="weekendAccess">
                            <span className={"label" + (editNotes.weekendAccess === 0 ? " makeitred" : "")}>Is there weekend access?</span>
                            <select className={"select" + (editNotes.weekendAccess === 0 ? " makeitred" : "")} id="weekendAccess" name="weekendAccess" value={editNotes.weekendAccess} onChange={(e) => handleNumberChange(e)}>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>

                        <label className="input-group" htmlFor="phoneCallRequired">
                            <span className={"label" + (editNotes.phoneCallRequired === 1 ? " makeitred" : "")}>Phone call required prior to arrival?</span>
                            <select className={"select" + (editNotes.phoneCallRequired === 1 ? " makeitred" : "")} id="phoneCallRequired" name="phoneCallRequired" value={editNotes.phoneCallRequired} onChange={(e) => handleNumberChange(e)}>
                                {yesNoOptions.map(yn =>
                                    <option key={yn.id} value={yn.id}>{yn.name}</option>
                                )};
                            </select>
                        </label>
                    </div>
                    <div className={editNotes.isEvent ? "" : "hidden"}>
                        <hr></hr>
                        <div className="makeitred">NOTE: This will be printed on the Runsheet for the Driver.</div>
                        <label className="input-group" htmlFor="eventStartDate">
                            <span className="label">Event Start</span>
                            <input className="input" type="datetime-local" id="eventStartDate" name="eventStartDate" value={editNotes.eventStartDate} onChange={(e) => handleEventChange(e)}></input>
                        </label>
                        <span className={errors["eventStartDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventStartDate"]}</span>
                        <label className="input-group" htmlFor="eventEndDate">
                            <span className="label">Event End</span>
                            <input className="input" type="datetime-local" id="eventEndDate" name="eventEndDate" value={editNotes.eventEndDate} onChange={(e) => handleEventChange(e)}></input>
                        </label>
                        <span className={errors["eventEndDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventEndDate"]}</span>
                        <label className="input-group" htmlFor="deliveryWindowStart">
                            <span className="label">Delivery Window Start</span>
                            <input className="input" type="datetime-local" id="deliveryWindowStart" name="deliveryWindowStart" value={editNotes.deliveryWindowStart} onChange={(e) => handleEventChange(e)}></input>
                        </label>
                        <span className={errors["deliveryWindowStart"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowStart"]}</span>
                        <label className="input-group" htmlFor="deliveryWindowEnd">
                            <span className="label">Delivery Window End</span>
                            <input className="input" type="datetime-local" id="deliveryWindowEnd" name="deliveryWindowEnd" value={editNotes.deliveryWindowEnd} onChange={(e) => handleEventChange(e)}></input>
                        </label>
                        <span className={errors["deliveryWindowEnd"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowEnd"]}</span>
                        <label className="input-group" htmlFor="deliveryPlacementNotes">
                            <span className="label">Delivery/Placement Notes</span>
                            <textarea className="input" rows={3} id="deliveryPlacementNotes" name="deliveryPlacementNotes" value={editNotes.deliveryPlacementNotes} onChange={(e) => handleEventChange(e)}></textarea>
                        </label>
                    </div>
                    <label className={editNotes.isEws && editNotes.extras != "" ? "input-group allocatepopup__nomargin" : "hidden"}>
                        <span className={editNotes.extras === "" ? "label label-medium" : "label label-medium label__red"}>Extras: {editNotes.extras}</span>
                    </label>
                </div>

                <div>
                    <label className="input-group--nowrap alignitemsleft" htmlFor="notes">
                        <span className="label">Schedule Notes</span>
                        <textarea className="input input-group__max--width" rows={4} id="notes" name="notes" value={editNotes.notes} onChange={(e) => handleNotesChange(e)}></textarea>
                    </label>
                    <label className={props.showDriverScheduleNotes ? "hidden" : "input-group--nowrap alignitemsleft"} htmlFor="runsheetNotes">
                        <span className="label">Runsheet Notes</span>
                        <span>{editNotes.runsheetAutoNotes}</span>
                        <textarea className="input input-group__max--width" rows={4} id="runsheetNotes" name="runsheetNotes" value={editNotes.runsheetNotes} onChange={(e) => handleNotesChange(e)}></textarea>
                    </label>
                    <label className="input-group--nowrap alignitemsleft" htmlFor="driverNotes">
                        <span className="label">Driver Notes</span>
                        <textarea className="input input-group__max--width" rows={4} id="driverNotes" name="driverNotes" value={editNotes.driverNotes} onChange={(e) => handleNotesChange(e)} disabled={!props.editDriverNotes}></textarea>
                    </label>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button className="popup-button--yes" onClick={save}>Save</button>
            </Modal.Footer>
        </Modal>
    );
    

    let contents =
        props.scheduleId < 1 && !props.showEditNotesModal ? "" :
        loading ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default EditNoteModal;